import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  UtensilsCrossed, Users, Plus, Minus, ShoppingCart, 
  Check, Search, ArrowLeft, Trash2, Send, Store, LogOut, RefreshCw,
  Clock, Bell, ChefHat, Receipt
} from 'lucide-react';
import { toast } from 'sonner';

// Order status config
const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-red-500', icon: Bell },
  preparing: { label: 'Preparing', color: 'bg-amber-500', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500', icon: Check },
  served: { label: 'Served', color: 'bg-blue-500', icon: UtensilsCrossed }
};

export default function TableOrder({ user, onLogout }) {
  // Check if popup mode
  const isPopup = new URLSearchParams(window.location.search).get('popup') === 'true';
  
  // Restaurant selection
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  
  // Tables and orders
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState({});
  
  // Menu
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['all']);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Cart for current order
  const [cart, setCart] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [viewOrdersDialogOpen, setViewOrdersDialogOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurant) {
      loadRestaurantData();
    }
  }, [selectedRestaurant]);

  const loadRestaurants = async () => {
    try {
      const res = await api.get('/restaurants');
      setRestaurants(res.data.filter(r => r.is_active !== false));
      if (res.data.length === 1) {
        setSelectedRestaurant(res.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurantData = async () => {
    setLoading(true);
    try {
      const [menuRes, tablesRes] = await Promise.all([
        api.get(`/menu?restaurant_id=${selectedRestaurant.id}`),
        api.get(`/tables?restaurant_id=${selectedRestaurant.id}`)
      ]);
      
      setMenuItems(menuRes.data.filter(item => item.is_available));
      setTables(tablesRes.data);
      
      // Extract categories
      const cats = ['all', ...new Set(menuRes.data.map(item => item.category).filter(Boolean))];
      setCategories(cats);
      
      // Load orders for occupied tables
      await loadTableOrders(tablesRes.data);
    } catch (error) {
      toast.error('Failed to load restaurant data');
    } finally {
      setLoading(false);
    }
  };

  const loadTableOrders = async (tableList) => {
    const orders = {};
    for (const table of tableList.filter(t => t.status === 'occupied')) {
      try {
        const res = await api.get(`/orders/table/${table.id}`);
        if (res.data.length > 0) {
          orders[table.id] = res.data;
        }
      } catch (e) {
        // Ignore
      }
    }
    setTableOrders(orders);
  };

  const getTableOrderCount = (tableId) => (tableOrders[tableId] || []).length;
  const getTableTotal = (tableId) => (tableOrders[tableId] || []).reduce((sum, o) => sum + (o.total_amount || 0), 0);

  // Cart functions
  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.id);
    if (existing) {
      setCart(cart.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { item_id: item.id, item_name: item.name, quantity: 1, price: item.price }]);
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.item_id === itemId) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : c;
      }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const clearCart = () => setCart([]);

  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const calculateTax = () => calculateSubtotal() * 0.18;
  const calculateTotal = () => calculateSubtotal() + calculateTax();

  // Place order
  const handlePlaceOrder = async () => {
    if (!selectedTable || cart.length === 0) return;

    try {
      const orderData = {
        restaurant_id: selectedRestaurant.id,
        order_type: 'dine_in',
        table_id: selectedTable.id,
        table_number: selectedTable.table_number,
        customer_name: `Table ${selectedTable.table_number}`,
        items: cart.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: calculateSubtotal(),
        discount: 0,
        tax: calculateTax(),
        notes: ''
      };

      await api.post('/orders', orderData);
      toast.success('Order sent to kitchen!');
      clearCart();
      setOrderDialogOpen(false);
      loadRestaurantData();
    } catch (error) {
      toast.error('Failed to place order');
    }
  };

  // Handle table click
  const handleTableClick = (table) => {
    setSelectedTable(table);
    if (table.status === 'occupied' && tableOrders[table.id]?.length > 0) {
      // Show existing orders first
      setViewOrdersDialogOpen(true);
    } else {
      // Open order dialog directly
      setCart([]);
      setOrderDialogOpen(true);
    }
  };

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Mobile container width
  const mobileWidth = isPopup ? 'max-w-md mx-auto' : '';

  // Restaurant Selection Screen
  if (!selectedRestaurant) {
    return (
      <div className={`${isPopup ? 'h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : ''}`}>
        <div className={`${mobileWidth} ${isPopup ? 'h-full flex flex-col p-4' : 'space-y-6'}`}>
          {/* Header */}
          <div className={`${isPopup ? 'text-center py-6' : ''}`}>
            {isPopup && (
              <div className="h-16 w-16 rounded-2xl bg-orange-500/20 flex items-center justify-center mx-auto mb-3">
                <UtensilsCrossed className="h-8 w-8 text-orange-400" />
              </div>
            )}
            <h1 className={`text-2xl font-bold ${isPopup ? 'text-white' : ''}`}>Table Orders</h1>
            <p className={`text-sm ${isPopup ? 'text-white/60' : 'text-muted-foreground'}`}>
              Select restaurant
            </p>
          </div>
          
          {/* Restaurant List */}
          <div className={`flex-1 ${isPopup ? 'overflow-y-auto' : ''}`}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className={`h-8 w-8 rounded-full border-4 ${isPopup ? 'border-orange-500' : 'border-primary'} border-t-transparent animate-spin`} />
              </div>
            ) : (
              <div className="space-y-3">
                {restaurants.map(restaurant => (
                  <Card 
                    key={restaurant.id}
                    className={`cursor-pointer active:scale-[0.98] transition-all ${isPopup ? 'bg-white/10 border-white/20' : ''}`}
                    onClick={() => setSelectedRestaurant(restaurant)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                        <UtensilsCrossed className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-bold truncate ${isPopup ? 'text-white' : ''}`}>{restaurant.name}</h3>
                        <p className={`text-sm ${isPopup ? 'text-white/50' : 'text-muted-foreground'}`}>
                          {restaurant.cuisine_type || 'Restaurant'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          
          {isPopup && onLogout && (
            <Button onClick={onLogout} variant="ghost" className="w-full text-white/60 hover:text-white hover:bg-white/10 mt-4">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${isPopup ? 'h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : ''}`}>
      <div className={`${mobileWidth} ${isPopup ? 'h-full flex flex-col' : 'space-y-6'}`}>
        {/* Header */}
        <div className={`flex items-center gap-3 ${isPopup ? 'p-4 bg-slate-800/50 border-b border-slate-700' : ''}`}>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSelectedRestaurant(null)}
            className={isPopup ? 'text-white/70 hover:text-white hover:bg-white/10' : ''}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className={`font-bold truncate ${isPopup ? 'text-white' : ''}`}>{selectedRestaurant.name}</h1>
            <p className={`text-xs ${isPopup ? 'text-white/50' : 'text-muted-foreground'}`}>
              {tables.length} tables
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={loadRestaurantData}
            className={isPopup ? 'text-white/70 hover:text-white hover:bg-white/10' : ''}
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {isPopup && onLogout && (
            <Button variant="ghost" size="icon" onClick={onLogout} className="text-white/70 hover:text-white hover:bg-white/10">
              <LogOut className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Tables Grid - Mobile optimized */}
        <div className={`flex-1 overflow-y-auto ${isPopup ? 'p-4' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
            </div>
          ) : tables.length === 0 ? (
            <div className={`text-center py-12 ${isPopup ? 'text-white/50' : 'text-muted-foreground'}`}>
              <Store className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No tables configured</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {tables.map(table => {
                const hasOrders = tableOrders[table.id]?.length > 0;
                const orderCount = getTableOrderCount(table.id);
                const total = getTableTotal(table.id);
                const isOccupied = table.status === 'occupied';
                
                return (
                  <Card 
                    key={table.id}
                    className={`cursor-pointer active:scale-[0.95] transition-all ${
                      isOccupied 
                        ? isPopup ? 'bg-orange-500/20 border-orange-400/50' : 'border-orange-400 bg-orange-50'
                        : isPopup ? 'bg-white/5 border-white/10' : ''
                    }`}
                    onClick={() => handleTableClick(table)}
                  >
                    <CardContent className="p-3 text-center">
                      <div className={`h-12 w-12 mx-auto rounded-full flex items-center justify-center mb-2 ${
                        isOccupied 
                          ? 'bg-orange-500 text-white' 
                          : isPopup ? 'bg-slate-700 text-white' : 'bg-gray-100'
                      }`}>
                        <span className="text-lg font-bold">{table.table_number}</span>
                      </div>
                      
                      <Badge className={`text-xs ${
                        isOccupied 
                          ? 'bg-orange-500 text-white' 
                          : isPopup ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'
                      }`}>
                        {isOccupied ? 'Occupied' : 'Free'}
                      </Badge>
                      
                      {hasOrders && (
                        <div className={`mt-2 pt-2 border-t ${isPopup ? 'border-white/10' : ''}`}>
                          <p className={`text-xs font-medium ${isPopup ? 'text-orange-300' : 'text-orange-600'}`}>
                            {orderCount} order{orderCount > 1 ? 's' : ''}
                          </p>
                          <p className={`text-sm font-bold ${isPopup ? 'text-white' : ''}`}>
                            ₹{total.toFixed(0)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* View Orders Dialog - For occupied tables */}
      <Dialog open={viewOrdersDialogOpen} onOpenChange={setViewOrdersDialogOpen}>
        <DialogContent className="max-w-sm mx-auto max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-500" />
              Table {selectedTable?.table_number} Orders
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-3">
              {(tableOrders[selectedTable?.id] || []).map((order, idx) => {
                const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                const StatusIcon = statusInfo.icon;
                
                return (
                  <Card key={order.id || idx} className="overflow-hidden">
                    <div className={`${statusInfo.color} px-3 py-1 flex items-center justify-between`}>
                      <span className="text-white text-xs font-medium flex items-center gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {statusInfo.label}
                      </span>
                      <span className="text-white/80 text-xs">
                        #{order.id?.slice(-6).toUpperCase()}
                      </span>
                    </div>
                    <CardContent className="p-3">
                      <div className="space-y-1">
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span>{item.quantity}x {item.item_name}</span>
                            <span className="text-muted-foreground">₹{(item.price * item.quantity).toFixed(0)}</span>
                          </div>
                        ))}
                      </div>
                      {order.notes && (
                        <div className="mt-2 text-xs bg-yellow-50 p-2 rounded text-yellow-700">
                          Note: {order.notes}
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {new Date(order.created_at).toLocaleTimeString()}
                        </span>
                        <span className="font-bold">₹{(order.total_amount || 0).toFixed(0)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
          
          <div className="flex gap-2 pt-4 border-t mt-4">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setViewOrdersDialogOpen(false)}
            >
              Close
            </Button>
            <Button 
              className="flex-1 bg-orange-500 hover:bg-orange-600"
              onClick={() => {
                setViewOrdersDialogOpen(false);
                setCart([]);
                setOrderDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-sm mx-auto max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-orange-500" />
              Table {selectedTable?.table_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search */}
            <div className="px-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-9"
                />
              </div>
            </div>
            
            {/* Categories */}
            <div className="px-4 pb-2">
              <ScrollArea className="w-full">
                <div className="flex gap-2">
                  {categories.map(cat => (
                    <Button
                      key={cat}
                      variant={selectedCategory === cat ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(cat)}
                      className="whitespace-nowrap h-7 text-xs"
                    >
                      {cat === 'all' ? 'All' : cat}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
            
            {/* Menu Items */}
            <ScrollArea className="flex-1 px-4">
              <div className="grid grid-cols-2 gap-2 pb-2">
                {filteredItems.map(item => (
                  <Card 
                    key={item.id}
                    className="cursor-pointer active:scale-[0.95] hover:bg-accent transition-all"
                    onClick={() => addToCart(item)}
                  >
                    <CardContent className="p-2">
                      <p className="font-medium text-xs truncate">{item.name}</p>
                      <p className="text-orange-600 font-bold text-sm">₹{item.price}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
            
            {/* Cart Summary */}
            {cart.length > 0 && (
              <div className="border-t bg-muted/50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Cart ({cart.reduce((sum, i) => sum + i.quantity, 0)})
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearCart} className="h-7 text-xs">
                    Clear
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-32 overflow-y-auto mb-3">
                  {cart.map(item => (
                    <div key={item.item_id} className="flex items-center gap-2 bg-background rounded p-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{item.item_name}</p>
                        <p className="text-xs text-muted-foreground">₹{item.price}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(item.item_id, -1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-5 text-center text-sm font-medium">{item.quantity}</span>
                        <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(item.item_id, 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-1 text-sm mb-3">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{calculateSubtotal().toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax (18%)</span>
                    <span>₹{calculateTax().toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base pt-1 border-t">
                    <span>Total</span>
                    <span>₹{calculateTotal().toFixed(0)}</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  onClick={handlePlaceOrder}
                  disabled={cart.length === 0}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send to Kitchen
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
